import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import FormService from "./FormService";
import ListServices from "./ListServices";
import { useNavigate, useParams } from "react-router-dom";
import { create, getById, update } from "../../services/contracts.service";
import { getAll as loadServices } from "../../services/services.service";
import { getAll as loadCustomers } from "../../services/customers.service";
import { getAll as loadPlans } from "../../services/plans.service";
import { getAll as loadSellers } from "../../services/users.service";
import { AlertService } from "../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import ConfirmModal from "../../components/Commons/Modal/confirm-modal.component";
import { DatePickerProps } from "antd/lib";
import moment from "moment";
import dayjs from "dayjs";
import { useLoggedUser } from "../../Hooks/useLoggedUser";
import { paymentMethods } from "../../Enums/PaymentMethod.enums";
import { contractsStatus } from "../../Enums/ContractsStatus.enums";
import { frequencies } from "../../Enums/Frequency.enums";
import { contractsRepeat } from "../../Enums/ContractsRepeat.enums";
import { getAccountDetails } from "../../services/accounts.service";
import { formatCurrency } from "../../services/utils";

const { Title } = Typography;
const { TextArea } = Input;

export default function FormContract(props: any) {
  const { loggedUser } = useLoggedUser();
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [billingDate, setBillingDate] = useState<string>("");
  const [services, setServices] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [sellers, setSellers] = useState<any[]>([]);
  const [contractServices, setContractServices] = useState<any[]>([]);
  const [sendEmailCharge, setSendEmailCharge] = useState<boolean>(true);
  const [autoBilling, setAutoBilling] = useState<boolean>(true);
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<any[]>(paymentMethods);

  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);

  const [totalServices, setTotalServices] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);

  const { uuid } = useParams();

  const [formContract] = Form.useForm();
  const paymentMethod = Form.useWatch("payment_method", formContract);
  const navigate = useNavigate();

  const settings = {
    create: { title: "Criar contrato", alertMessage: "gravada" },
    edit: { title: "Editar contrato", alertMessage: "editada" },
    duplicate: { title: "Duplicar contrato", alertMessage: "duplicada" },
  };

  const goContractsList = () => {
    navigate(`/contracts`);
  };

  const onAddService = (values: any) => {
    setContractServices(contractServices.concat(values));
    setValuesChanged(true);
  };

  const onUpdateContractService = (service_id: any, values: any) => {
    let _contractServices = [...contractServices];

    let idx = _contractServices.findIndex(
      (item) => item.service_id == service_id
    );

    if (idx > -1) {
      _contractServices[idx] = values;

      setContractServices(_contractServices);
      setValuesChanged(true);
    }
  };

  const onRemoveService = (service_id: any) => {
    setContractServices(
      contractServices.filter(
        (tableService: any) => tableService.service_id != service_id
      )
    );
    setValuesChanged(true);
  };

  const { isLoading: loadingContract } = useQuery(
    ["loadContract", uuid],
    () => (uuid ? getById(uuid) : null),
    {
      onSuccess: (response) => {
        if (response) {
          const values = response.data;

          setContractServices(values.contractServices);
          setSendEmailCharge(values.send_email_charge);
          setAutoBilling(values.auto_billing);

          values.start_date = dayjs(values?.start_date);
          setStartDate(values?.start_date);

          if (values?.end_date) {
            values.end_date = dayjs(values?.end_date);
            setEndDate(values?.end_date);
          }

          if (values?.billing_date) {
            values.billing_date = dayjs(values?.billing_date);
            setBillingDate(values?.billing_date);
          }

          formContract.setFieldsValue(values);
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  useQuery(["loadCustomers"], () => loadCustomers({ status: "active" }), {
    onSuccess: (response) => {
      setCustomers(
        response.data.map((customer: any) => {
          return {
            key: customer.data.id,
            value: customer.data.id,
            label: customer.data.name,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["loadPlans"], () => loadPlans({ status: "active" }), {
    onSuccess: (response) => {
      setPlans(
        response.data.map((plan: any) => {
          return {
            key: plan.data.id,
            value: plan.data.id,
            label: `${plan.data.name} (${formatCurrency(plan.data.price)})`,
            discount: plan.data.discount,
            services: plan.data.services_info,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["loadSellers"], () => loadSellers({ status: "active" }), {
    onSuccess: (response) => {
      setSellers(
        response.data.map((seller: any) => {
          return {
            key: seller.id,
            value: seller.id,
            label: seller.name,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["loadServices"], () => loadServices({ status: "active" }), {
    onSuccess: (response) => {
      setServices(
        response.data.map((service: any) => {
          return {
            key: service.data.id,
            value: service.data.id,
            label: service.data.name,
            price: service.data.value,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["getAccountDetails"], () => getAccountDetails(), {
    onSuccess: (response) => {
      const settings =
        typeof response?.data?.settings === "string"
          ? JSON.parse(response?.data?.settings)
          : response?.data?.settings || {};

      const allowed = paymentMethods.filter((m) =>
        (settings?.payment_methods || []).includes(m.value)
      );

      const options = allowed.length ? allowed : paymentMethods;
      setAllowedPaymentMethods(options);

      const current = formContract.getFieldValue("payment_method");
      if (!options.some((m) => m.value === current)) {
        formContract.setFieldValue("payment_method", options[0]?.value);
      }
    },
    refetchOnWindowFocus: false,
  });

  const { mutate: createContract, isLoading: createContractLoading } =
    useMutation(create, {
      onSuccess: (response) => {
        AlertService.sendAlert([
          {
            text: `O contrato foi ${
              settings[props.action as keyof typeof settings].alertMessage
            } com sucesso.`,
            nextPage: true,
          },
        ]);

        goContractsList();
      },
    });

  const { mutate: updateContract, isLoading: updateContractLoading } =
    useMutation(update, {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: `O contrato foi ${
              settings[props.action as keyof typeof settings].alertMessage
            } com sucesso.`,
            nextPage: true,
          },
        ]);

        goContractsList();
      },
    });

  const onChangeStartDate: DatePickerProps["onChange"] = (date, dateString) => {
    setStartDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onChangeEndDate: DatePickerProps["onChange"] = (date, dateString) => {
    setEndDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onChangeBillingDate: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {
    setBillingDate(moment(dateString, "DD/MM/YYYY").format("YYYY-MM-DD"));
  };

  const onChangePlan = (plan_id: number) => {
    let plan = plans.find((plan) => plan.value === plan_id);

    if (!plan) return;

    formContract.setFieldValue("discount", plan.discount);

    setContractServices(
      plan.services
        .filter((service: any) => service.active)
        .map((service: any) => ({
          service_id: service.id,
          price: service.value,
          quantity: 1,
        }))
    );
  };

  const onSubmit = () => {
    if (!contractServices.length) {
      AlertService.sendAlert([
        {
          text: "É obrigatório adicionar ao menos um serviço para prosseguir.",
          type: "error",
        },
      ]);

      return;
    }

    formContract
      .validateFields()
      .then((values) => {
        values.start_date =
          typeof startDate === "object"
            ? dayjs(startDate).format("YYYY-MM-DD")
            : startDate;

        values.end_date =
          typeof endDate === "object"
            ? dayjs(endDate).format("YYYY-MM-DD")
            : endDate;

        values.billing_date =
          typeof billingDate === "object"
            ? dayjs(billingDate).format("YYYY-MM-DD")
            : billingDate;

        values.services = contractServices.map((service) => ({
          id: service.service_id,
          price: service.price,
          quantity: service.quantity,
        }));

        if (!uuid || props.action === "duplicate") {
          createContract(values);
        } else {
          updateContract({
            values,
            uuid,
          });
        }
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  const calcTotalAmount = () => {
    let discount = formContract.getFieldValue("discount") || 0;
    let total = totalServices - (totalServices * discount) / 100;

    formContract.setFieldValue("total_amount", total);

    setTotalAmount(total);
  };

  const onChangeSendEmailCharge = (value: boolean) => {
    setSendEmailCharge(value);
  };

  const onChangeAutoBilling = (value: boolean) => {
    setAutoBilling(value);
  };

  useEffect(() => {
    if (!uuid) {
      const today = new Date();
      const _date = dayjs(
        today.getFullYear() +
          "-" +
          (today.getMonth() + 1) +
          "-" +
          today.getDate()
      );

      formContract.setFieldsValue({
        start_date: _date,
        seller_id: loggedUser?.id || null,
        discount: 0,
        payment_method: "Pix",
        status: "Active",
        frequency: "Monthly",
        repeat: "Always",
        auto_billing: true,
      });

      setStartDate(
        today.getFullYear() +
          "-" +
          (today.getMonth() + 1) +
          "-" +
          today.getDate()
      );
    }
  }, [uuid]);

  useEffect(() => {
    setTotalServices(
      contractServices.reduce(
        (total, service) => (total += service.price * service.quantity),
        0
      )
    );
  }, [contractServices]);

  useEffect(() => {
    calcTotalAmount();
  }, [totalServices]);

  return (
    <>
      <Card
        color="primary"
        styles={{
          header: {
            color: "#107B99",
            fontWeight: 600,
          },
        }}
        title={settings[props.action as keyof typeof settings].title}
        loading={loadingContract}
      >
        <Form
          form={formContract}
          name="form_table"
          layout="vertical"
          autoComplete="off"
        >
          <Row gutter={20}>
            <Col span={24}>
              <Title level={5} style={{ marginTop: 0 }}>
                Dados do Contrato
              </Title>
            </Col>

            <Col
              lg={{ span: 16 }}
              md={{ span: 16 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Cliente"
                name="customer_id"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o cliente"
                  size="large"
                  options={customers}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Plano"
                name="plan_id"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o plano"
                  size="large"
                  options={plans}
                  onChange={onChangePlan}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                name="start_date"
                label="Data de Início"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <DatePicker
                  size="large"
                  placeholder="Data de Início"
                  showNow={true}
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  onChange={onChangeStartDate}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item name="end_date" label="Data de Fim">
                <DatePicker
                  size="large"
                  placeholder="Data de Fim"
                  showNow={true}
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  onChange={onChangeEndDate}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Situação"
                name="status"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o status do contrato"
                  size="large"
                  options={contractsStatus}
                  disabled={!!uuid && props.action === "edit"}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Vendedor"
                name="seller_id"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o vendedor"
                  size="large"
                  options={sellers}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={20}>
            <Col span={24}>
              <Title level={5} style={{ marginTop: 0 }}>
                Dados da Recorrência
              </Title>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item name="billing_date" label="Data da Emissão">
                <DatePicker
                  size="large"
                  placeholder="Data da Emissão"
                  showNow={true}
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  onChange={onChangeBillingDate}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Frequência"
                name="frequency"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione a frequência do contrato"
                  size="large"
                  options={frequencies}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Repetir"
                name="repeat"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o mode de repetição do contrato"
                  size="large"
                  options={contractsRepeat}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Geração automática" name="auto_billing">
                <Switch checked={autoBilling} onChange={onChangeAutoBilling} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={20}>
            <Col span={24}>
              <Title level={5} style={{ marginTop: 0 }}>
                Itens do Contrato
              </Title>
            </Col>

            <Col span={24}>
              <FormService
                services={services}
                existedServices={contractServices}
                loading={createContractLoading || updateContractLoading}
                onAddService={onAddService}
              />
            </Col>

            <Col span={24}>
              <ListServices
                services={services}
                contractServices={contractServices}
                totalServices={totalServices}
                onRemoveService={onRemoveService}
                onUpdateContractService={onUpdateContractService}
              />
            </Col>
          </Row>

          <Row gutter={20} style={{ marginTop: 30 }}>
            <Col
              lg={{ span: 4 }}
              md={{ span: 4 }}
              sm={{ span: 12 }}
              xs={{ span: 12 }}
            >
              <Form.Item label="Desconto %" name="discount">
                {}
                <InputNumber
                  min={0}
                  size="large"
                  style={{ width: "100%" }}
                  maxLength={5}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }
                  onChange={() => calcTotalAmount()}
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 4 }}
              md={{ span: 4 }}
              sm={{ span: 12 }}
              xs={{ span: 12 }}
            >
              <Form.Item
                label="Valor do Contrato"
                name="total_amount"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <InputNumber
                  min={1}
                  size="large"
                  style={{ width: "100%", display: "none" }}
                  maxLength={5}
                  disabled
                />
                <b>{formatCurrency(totalAmount)}</b>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Form.Item label="Discriminação" name="description">
                <TextArea size="large" showCount maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={20}>
            <Col span={24}>
              <Title level={5} style={{ marginTop: 0 }}>
                Cobrança
              </Title>
            </Col>

            <Col
              lg={{ span: 6 }}
              md={{ span: 6 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Meio de Pagamento"
                name="payment_method"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  placeholder="Selecione o método de pagamento"
                  size="large"
                  options={allowedPaymentMethods}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Enviar cobrança por e-mail"
                name="send_email_charge"
              >
                <Switch
                  checked={sendEmailCharge}
                  onChange={onChangeSendEmailCharge}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider />

        <Row
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Col></Col>
          <Col>
            <Space>
              <Button
                loading={createContractLoading || updateContractLoading}
                size="large"
                onClick={() => {
                  if (!uuid || valuesChanged) {
                    setLeaveThePage(true);
                  } else {
                    goContractsList();
                  }
                }}
              >
                Voltar
              </Button>

              <Button
                type="primary"
                loading={createContractLoading || updateContractLoading}
                size="large"
                onClick={onSubmit}
              >
                Gravar
              </Button>
            </Space>
          </Col>
        </Row>

        <ConfirmModal
          open={leaveThePage}
          title="Tem certeza de que deseja sair sem gravar as alterações?"
          content="Toda a informação não gravada será perdida."
          okText="Sair"
          cancelText="Voltar"
          onOk={() => {
            setLeaveThePage(false);
            goContractsList();
          }}
          onCancel={() => setLeaveThePage(false)}
        ></ConfirmModal>
      </Card>
    </>
  );
}
